import React, { useState, useEffect, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Save, AlertTriangle, Upload, Shield, X, Check } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const FRAMES = [
  "02d111604ea5309ce7dcae5d61cafbb28a61ed57.png", "067e9e218dfe2b5ced541d54daa7f28aaab4354f.png", "083c19cc935001ee0508aff3b948da62b6a093f6.png",
  "0bc16c5a0d58517c8da5f77ee003f743c5d044f3 - Copie.png", "0ufCAd8 - Copie.png", "12bb5b310693650c6c5263af99f69d3c25fd771e.png",
  "19792ed4a16491c5281b98a27df20754675c0bbc.png", "1a05a372467f6f34d27014939133395ae9259ca4 - Copie.png", "2f18eab87a99138ae72ff8e8c22108efa72cd3d8 - Copie.png",
  "36f7893520284c0797e30d08c953a0cf4db9260d.png", "3e77c41368aea83154676e71e4de03d627163607 - Copie.png", "3e77c41368aea83154676e71e4de03d627163607.png",
  "42a7896707074b1b8ebbba7f5d0b2d0b91e48512.png", "46461aaea39b18a4a3da2e6d3cf253006f2d6193.png", "5a089a053f43b3cf7f848b578c26ad46bd68e64f.png",
  "5b4be2c7e9dac5355f5bd5589f5601092548b252 - Copie.png", "5e994bb77a87fe9e2fbf49a9c96b9959ac13aa12 - Copie.png", "5e994bb77a87fe9e2fbf49a9c96b9959ac13aa12.png",
  "5TOM5dx.png", "5v6PzY8.png", "66168bac76b1f448d4028a34a991ad377e1a2153.png", "8Ad8hb0.png", "8fD9ulM.png",
  "99e0f27ac1cd7117b342b035d8b725ddb1b76d28.png", "bbeee526d6569be90b601d31c7177cc66f9d9fa0.png", "bbMEuqy.png", "BXkdmib.png",
  "cb74dd91a84b013df09ab337f6d8d91e9b427bfa.png", "ce66b59f22e5d1eb23202ae86e64d35486525310.png", "cT2QZmr.png", "d53ca26380b75490cece13536cedab5cf4fd59fa.png",
  "DLXEMTq.png", "eae97432526b1f3d24194ae8c9b73d471a1a26de.png", "ffcd7cb08f88f2435f20657b019be15fe5a6613b.png", "LOOWXjr.png",
  "m5vnDml.png", "mpJztdO.png", "NOmagWW.png", "QOIiZFp.png", "RZv4gRY.png", "ssteam8.png", "steam2.png",
  "steam3.png", "steam4.png", "SZQ38tU.png", "digirain.gif",
  "frame_01.png", "frame_02.png", "frame_03.png", "frame_04.png", "frame_05.png", "frame_06.png", "frame_07.png", "frame_08.png",
  "frame_09.png", "frame_10.png", "frame_11.png", "frame_12.png", "frame_13.png", "frame_14.png", "frame_15.png", "frame_16.png",
  "frame_17.png", "frame_18.png", "frame_19.png", "frame_20.png", "frame_21.png", "frame_22.png", "frame_23.png", "frame_24.png",
  "frame_25.png", "frame_26.png", "frame_27.png", "frame_28.png", "frame_29.png", "frame_30.png", "frame_31.png", "frame_32.png",
  "frame_33.png", "frame_34.png", "frame_35.png", "frame_36.png", "frame_37.png", "frame_38.gif", "frame_39.png", "frame_40.png",
  "frame_41.png", "frame_42.png", "frame_43.png", "frame_44.png", "frame_45.png", "frame_46.png"
];

export default function ClanSettings({ clan, onClose }) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: "",
    tag: "",
    motto: "",
    description: "",
    logo_url: "",
    banner_url: "",
    avatar_frame: "",
    accent_color: "#ea580c",
    max_members: 100,
    is_recruiting: true,
    is_private: false,
    discord: "",
    steam: "",
    website: ""
  });
  
  const [leaderUsername, setLeaderUsername] = useState("");

  useEffect(() => {
    if (clan) {
      setFormData({
        name: clan.name || "",
        tag: clan.tag || "",
        motto: clan.motto || "",
        description: clan.description || "",
        logo_url: clan.logo_url || "",
        banner_url: clan.banner_url || "",
        avatar_frame: clan.avatar_frame || "",
        accent_color: clan.accent_color || "#ea580c",
        max_members: clan.max_members || 100,
        is_recruiting: clan.is_recruiting ?? true,
        is_private: clan.is_private ?? false,
        discord: clan.social_links?.discord || "",
        steam: clan.social_links?.steam || "",
        website: clan.social_links?.website || ""
      });
      
      const leader = clan.roster?.find(m => m.role === "leader");
      if (leader) setLeaderUsername(leader.username);
    }
  }, [clan]);

  const updateMutation = useMutation({
    mutationFn: async (data) => {
      await api.put(`/clans/${clan.id || clan._id}`, data);
    },
    onSuccess: () => {
      toast.success("Setări salvate cu succes!");
      queryClient.invalidateQueries(["clan", clan.id || clan._id]);
      if (onClose) onClose();
    },
    onError: (err) => {
      toast.error(formatApiError(err) || "Eroare la salvarea setărilor");
    }
  });

  const uploadMutation = useMutation({
    mutationFn: async ({ file, field }) => {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/upload", form, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      return { url: `/app-api${res.data.url}`, field };
    },
    onSuccess: (data) => {
      setFormData(prev => ({ ...prev, [data.field]: data.url }));
      toast.success("Imagine încărcată cu succes!");
    },
    onError: (err) => toast.error(formatApiError(err) || "Eroare la încărcare")
  });

  const handleFileChange = (e, field) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadMutation.mutate({ file, field });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex items-center justify-between border-b border-white/10 pb-4">
        <h3 className="text-lg font-black text-white font-chakra uppercase tracking-wider flex items-center gap-2">
          <Shield className="w-5 h-5 text-[var(--tf-primary)]" />
          <span>Panou Administrare & Setări Clan</span>
        </h3>
        {onClose && (
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white p-1">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-bold text-zinc-400 mb-1 font-chakra uppercase tracking-wider">Nume Clan *</label>
          <input 
            type="text" 
            required 
            className="w-full bg-[#07090e] border border-white/10 px-3 py-2 text-xs font-mono text-white focus:border-[var(--tf-primary)] focus:outline-none" 
            value={formData.name} 
            onChange={e => setFormData({...formData, name: e.target.value})} 
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-zinc-400 mb-1 font-chakra uppercase tracking-wider">Tag Clan *</label>
          <input 
            type="text" 
            required 
            className="w-full bg-[#07090e] border border-white/10 px-3 py-2 text-xs font-mono text-white focus:border-[var(--tf-primary)] focus:outline-none font-bold uppercase tracking-widest" 
            value={formData.tag} 
            onChange={e => setFormData({...formData, tag: e.target.value.toUpperCase()})} 
            maxLength={5} 
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-bold text-zinc-400 mb-1 font-chakra uppercase tracking-wider">Slogan / Motto Clan</label>
        <input 
          type="text" 
          className="w-full bg-[#07090e] border border-white/10 px-3 py-2 text-xs font-mono text-white focus:border-[var(--tf-primary)] focus:outline-none" 
          value={formData.motto} 
          onChange={e => setFormData({...formData, motto: e.target.value})} 
          placeholder="ex: Play to dominate, stay legendary." 
        />
      </div>

      <div className="bg-[#07090e] border border-[var(--tf-primary)]/30 p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-[var(--tf-primary)] shrink-0 mt-0.5" />
        <div>
          <label className="block text-xs font-bold text-[var(--tf-primary)] mb-1 font-chakra uppercase tracking-wider">Lider Clan (Username)</label>
          <input 
            type="text" 
            disabled={true} 
            className="w-full bg-black/60 border border-white/10 px-3 py-2 text-xs font-mono text-zinc-400 opacity-70 cursor-not-allowed" 
            value={leaderUsername} 
          />
          <p className="text-[10px] text-zinc-400 font-mono mt-1.5 leading-relaxed">
            * Pentru a transfera leadership-ul, folosiți butonul "LIDER" din tabul "Participanți" din pagina clanului.
          </p>
        </div>
      </div>

      <div>
        <label className="block text-xs font-bold text-zinc-400 mb-1 font-chakra uppercase tracking-wider">Logo / Avatar Clan</label>
        <div className="flex flex-col sm:flex-row gap-3">
          <input 
            type="text" 
            className="flex-1 bg-[#07090e] border border-white/10 px-3 py-2 text-xs font-mono text-white focus:border-[var(--tf-primary)] focus:outline-none" 
            value={formData.logo_url} 
            onChange={e => setFormData({...formData, logo_url: e.target.value})} 
            placeholder="Link direct sau încarcă fișier..." 
          />
          <div className="flex gap-2">
            <input type="file" accept="image/*" className="hidden" ref={logoInputRef} onChange={e => handleFileChange(e, 'logo_url')} />
            <button 
              type="button" 
              onClick={() => logoInputRef.current?.click()} 
              disabled={uploadMutation.isPending}
              className="bg-white/5 hover:bg-white/10 text-white px-4 py-2 border border-white/10 text-xs font-chakra font-bold flex items-center gap-2 transition-colors whitespace-nowrap"
            >
              <Upload className="w-4 h-4" /> Încarcă din PC
            </button>
            {formData.logo_url && (
              <div className="w-9 h-9 border border-white/10 overflow-hidden shrink-0 bg-black">
                <img src={formData.logo_url} alt="Logo" className="w-full h-full object-cover" />
              </div>
            )}
          </div>
        </div>
      </div>

      <div>
        <label className="block text-xs font-bold text-zinc-400 mb-1 font-chakra uppercase tracking-wider">Ramă Avatar Clan (Frame)</label>
        <div className="bg-[#07090e] border border-white/10 p-3 max-h-56 overflow-y-auto">
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2.5">
            <div 
              onClick={() => setFormData({...formData, avatar_frame: ""})}
              className={`cursor-pointer border flex items-center justify-center p-1.5 h-14 transition-all ${!formData.avatar_frame ? 'border-[var(--tf-primary)] bg-[var(--tf-primary)]/20' : 'border-white/10 hover:bg-white/5'}`}
            >
              <span className="text-[10px] text-zinc-400 font-bold font-chakra uppercase text-center">Fără<br/>Ramă</span>
            </div>
            {FRAMES.map((f, i) => (
              <div 
                key={i} 
                onClick={() => setFormData({...formData, avatar_frame: f})}
                className={`cursor-pointer border p-1 transition-all flex items-center justify-center h-14 ${formData.avatar_frame === f ? 'border-[var(--tf-primary)] bg-[var(--tf-primary)]/20' : 'border-white/10 hover:bg-white/5'}`}
              >
                <img 
                  src={encodeURI(`${process.env.PUBLIC_URL || ''}/frames/${f}`)} 
                  alt="" 
                  className="max-h-full max-w-full object-contain"
                  onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div>
        <label className="block text-xs font-bold text-zinc-400 mb-1 font-chakra uppercase tracking-wider">Banner Clan</label>
        <div className="flex flex-col sm:flex-row gap-3 mb-2">
          <input 
            type="text" 
            className="flex-1 bg-[#07090e] border border-white/10 px-3 py-2 text-xs font-mono text-white focus:border-[var(--tf-primary)] focus:outline-none" 
            value={formData.banner_url} 
            onChange={e => setFormData({...formData, banner_url: e.target.value})} 
            placeholder="Link direct sau încarcă fișier..." 
          />
          <input type="file" accept="image/*" className="hidden" ref={bannerInputRef} onChange={e => handleFileChange(e, 'banner_url')} />
          <button 
            type="button" 
            onClick={() => bannerInputRef.current?.click()} 
            disabled={uploadMutation.isPending}
            className="bg-white/5 hover:bg-white/10 text-white px-4 py-2 border border-white/10 text-xs font-chakra font-bold flex items-center gap-2 transition-colors whitespace-nowrap"
          >
            <Upload className="w-4 h-4" /> Încarcă din PC
          </button>
        </div>
        {formData.banner_url && (
          <div className="h-20 border border-white/10 overflow-hidden w-full max-w-sm bg-black">
            <img src={formData.banner_url} alt="Banner" className="w-full h-full object-cover" />
          </div>
        )}
      </div>

      <div>
        <label className="block text-xs font-bold text-zinc-400 mb-1 font-chakra uppercase tracking-wider">Culoare Accent (Design UI)</label>
        <div className="flex items-center gap-3">
          <input 
            type="color" 
            className="h-9 w-20 bg-transparent border-0 cursor-pointer" 
            value={formData.accent_color} 
            onChange={e => setFormData({...formData, accent_color: e.target.value})} 
          />
          <div className="flex-1 h-8 flex items-center px-3 border border-white/10 font-chakra font-black text-xs text-black uppercase" style={{ backgroundColor: formData.accent_color }}>
            Previzualizare Culoare Accent
          </div>
        </div>
      </div>

      <div>
        <label className="block text-xs font-bold text-zinc-400 mb-1 font-chakra uppercase tracking-wider">Descriere / Despre Noi</label>
        <textarea 
          className="w-full bg-[#07090e] border border-white/10 px-3 py-2 text-xs font-mono text-white focus:border-[var(--tf-primary)] focus:outline-none min-h-[90px]" 
          value={formData.description} 
          onChange={e => setFormData({...formData, description: e.target.value})} 
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-bold text-zinc-400 mb-1 font-chakra uppercase tracking-wider">Limită Membri</label>
          <input 
            type="number" 
            className="w-full bg-[#07090e] border border-white/10 px-3 py-2 text-xs font-mono text-white focus:border-[var(--tf-primary)] focus:outline-none" 
            value={formData.max_members} 
            onChange={e => setFormData({...formData, max_members: parseInt(e.target.value) || 100})} 
            min="1" 
            max="100" 
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-zinc-400 mb-1 font-chakra uppercase tracking-wider">Recrutare Deschisă?</label>
          <select 
            className="w-full bg-[#07090e] border border-white/10 px-3 py-2 text-xs font-mono text-white focus:border-[var(--tf-primary)] focus:outline-none cursor-pointer"
            value={formData.is_recruiting ? "true" : "false"} 
            onChange={e => setFormData({...formData, is_recruiting: e.target.value === "true"})}
          >
            <option value="true">Da — acceptăm cereri de alăturare</option>
            <option value="false">Nu — recrutare închisă</option>
          </select>
        </div>
      </div>

      <div className="bg-[#0a0d14] border border-amber-500/20 p-4 mt-1">
        <label className="block text-xs font-bold text-zinc-400 mb-2 font-chakra uppercase tracking-wider">🔒 Vizibilitate Clan</label>
        <select 
          className="w-full bg-[#07090e] border border-white/10 px-3 py-2 text-xs font-mono text-white focus:border-amber-500/50 focus:outline-none cursor-pointer"
          value={formData.is_private ? "private" : "public"} 
          onChange={e => setFormData({...formData, is_private: e.target.value === "private"})}
        >
          <option value="public">🌐 Public — vizibil tuturor utilizatorilor</option>
          <option value="private">🔒 Privat — doar membrii văd detaliile confidențiale</option>
        </select>
        <p className="text-[10px] text-zinc-500 font-mono mt-1.5">
          Clanurile private ascund tab-urile Participanți, Activitate, Anunțuri și IDG Coins de vizitatori și non-membri.
        </p>
      </div>

      <div className="space-y-3">
        <div>
          <label className="block text-xs font-bold text-zinc-400 mb-1 font-chakra uppercase tracking-wider">Link Discord</label>
          <input 
            type="url" 
            className="w-full bg-[#07090e] border border-white/10 px-3 py-2 text-xs font-mono text-white focus:border-[var(--tf-primary)] focus:outline-none" 
            value={formData.discord} 
            onChange={e => setFormData({...formData, discord: e.target.value})} 
            placeholder="https://discord.gg/..." 
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-zinc-400 mb-1 font-chakra uppercase tracking-wider">Link Grup Steam</label>
          <input 
            type="url" 
            className="w-full bg-[#07090e] border border-white/10 px-3 py-2 text-xs font-mono text-white focus:border-[var(--tf-primary)] focus:outline-none" 
            value={formData.steam} 
            onChange={e => setFormData({...formData, steam: e.target.value})} 
            placeholder="https://steamcommunity.com/groups/..." 
          />
        </div>
      </div>

      <div className="pt-4 border-t border-white/10 flex items-center justify-end gap-3">
        {onClose && (
          <button 
            type="button" 
            onClick={onClose} 
            className="bg-white/5 hover:bg-white/10 text-zinc-300 px-5 py-2.5 text-xs font-chakra font-bold uppercase tracking-wider border border-white/10"
          >
            Anulează
          </button>
        )}
        <button 
          type="submit" 
          disabled={updateMutation.isPending}
          className="tf-btn-primary tf-clip-btn px-6 py-2.5 text-xs font-chakra font-black uppercase tracking-wider shadow-lg flex items-center gap-2"
          style={{ backgroundColor: formData.accent_color || "var(--tf-primary)" }}
        >
          <Save className="w-4 h-4" />
          <span>{updateMutation.isPending ? "Se salvează..." : "Salvează Modificările"}</span>
        </button>
      </div>
    </form>
  );
}
